/**
 * Drivers nurture + post-delivery WhatsApp bodies (free-form Baileys sends).
 *
 * The drivers vertical sends WhatsApp exclusively over a ChatSuite Baileys
 * session (`+41765022800`) since the 2026-06-30 WABA ban. Baileys cannot send
 * Meta `templateMessage` (dropped in 7.0.0-rc.9), so the nurture + post-delivery
 * sequence steps that used a Meta `templateId` failed 100% (they never left the
 * provider). These builders replace those template steps with free-form text —
 * the same lane the one-shot opener (`opener-variants.ts`) and the Hero
 * finish-signup nudge (`hero-nudge-segment.ts`) already deliver on.
 *
 * Every builder is a pure function of the captured lead (trivially
 * unit-testable) and SERVICE-scope: a transactional follow-up to the driver's
 * OWN application. They MUST stay free of the marketing lexicon the
 * sender-gateway's category-at-send guard scans for (`findMarketingLexicon`:
 * "earn", "welcome", "get started", "offer", "flexible hours", …) or a
 * `service` send is blocked as `category_mismatch`. Personalization degrades
 * safely: `resolveNameCity` falls back to "there" for a missing name and drops
 * the ` in {city}` clause for empty / placeholder cities — so no raw `{{var}}`
 * ever renders (per PR #387).
 */

import type { FirstMessageLead } from '@selenos/leads';

import { resolveNameCity } from './opener-variants';

/**
 * `driver-nurture` step 0 (`driver-nurture-profile-whatsapp`, +60min).
 *
 * The driver applied but has not finished answering the qualification
 * questions. Ask for the two highest-value delivery-gate answers (licence +
 * vehicle) with a low-friction reply.
 */
export function renderDriverNurtureProfileBody(lead: FirstMessageLead): string {
  const { name, where } = resolveNameCity(lead);
  return (
    `Hi ${name}, it's Naledi from Rovvo, following up on your application to drive${where}. ` +
    `To move things forward we just need a couple of quick answers: do you have a valid ` +
    `driver's licence, and do you have access to a car? Reply here and we'll help you with ` +
    `the next step.`
  );
}

/**
 * `driver-nurture` step 2 (`driver-nurture-reminder-whatsapp`, +3 days,
 * `not_engaged`).
 *
 * A gentle reminder for a driver who has not replied to the profile follow-up.
 * Gives an easy out so a driver who has moved on is not pressured.
 */
export function renderDriverNurtureReminderBody(
  lead: FirstMessageLead,
): string {
  const { name, where } = resolveNameCity(lead);
  return (
    `Hi ${name}, it's Naledi from Rovvo, just checking back on your application to ` +
    `drive${where}. If you still want to set up your Uber driver account, reply here with ` +
    `any questions and we'll help you finish. If now isn't the right time, no problem, ` +
    `just let us know.`
  );
}

/**
 * `rideshare-post-delivery` step 0 (`rideshare-post-delivery-nudge-whatsapp`,
 * +24h post-delivery).
 *
 * The application cleared to Uber and Uber has sent the signup invite. Point
 * the driver at the self-serve document upload in the Uber Driver app (we
 * cannot upload on their behalf) so they finish signup.
 */
export function renderPostDeliveryNudgeBody(lead: FirstMessageLead): string {
  const { name } = resolveNameCity(lead);
  return (
    `Hi ${name}, it's Naledi from Rovvo. Your Uber driver application has gone through, ` +
    `and Uber has sent you an invite to finish signing up. To complete it, open the Uber ` +
    `Driver app, tap Account > Documents, and upload your PrDP, driver's licence, profile ` +
    `photo, and vehicle documents. Reply here if you need a hand.`
  );
}

/**
 * `rideshare-post-delivery` step 2 (`rideshare-post-delivery-final-whatsapp`,
 * +7 days, `not_engaged`).
 *
 * The final touch for a driver who has not finished Uber signup. Notes the
 * invite may expire without inventing a hard deadline ("last chance" / "limited
 * time" are marketing-lexicon triggers and are deliberately avoided).
 */
export function renderPostDeliveryFinalBody(lead: FirstMessageLead): string {
  const { name } = resolveNameCity(lead);
  return (
    `Hi ${name}, it's Naledi from Rovvo, last check-in on your Uber signup. Your invite ` +
    `may expire soon, so if you still want to drive, open the Uber Driver app and finish ` +
    `uploading your documents (PrDP, driver's licence, profile photo, and vehicle papers). ` +
    `Reply here if anything is holding you up and we'll help.`
  );
}
